import { fromJS } from 'immutable';
import { handleActions } from 'redux-actions';
import {
  CHARTIQ_STUDY_ADD_STUDY,
  CHARTIQ_STUDY_CLOSE_STUDY_MODAL,
  CHARTIQ_STUDY_OPEN_STUDY_MODAL,
  CHARTIQ_STUDY_REMOVE_STUDY,
  CHARTIQ_STUDY_TOGGLE_STUDY_OVERLAY,
  CHARTIQ_STUDY_UPDATE_STUDY,
} from './constants';
import moment from 'moment';

const CIQ = (window as any).CIQ;

const good = '#00CD82';
const bad = '#FF3542';

const obosInlineStudy = {
  inputs: {},
  name: '_Overbought/Oversold Inline',
  outputs: {
    Percentile10: { color: good, width: 3, pattern: 'solid' },
    Percentile90: { color: bad, width: 3, pattern: 'solid' },
  },
  overlay: true,
  seriesFN: (stx: any, sd: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, sd, quotes);
  },
};

const LAWRStudy = {
  inputs: { Period: 18, Field: 'Close', Type: 'simple', Offset: 0 },
  name: '_LAWR',
  outputs: {
    LAWR: { color: '#f48fb1', width: 3, pattern: 'solid' },
  },
  overlay: true,
  seriesFN: (stx: any, sd: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, sd, quotes);
  },
};

const obosIndividualStudy = {
  inputs: {},
  name: '_Overbought/Oversold Individual',
  outputs: {
    Tftf10_open: good,
    Tftf90_open: bad,
  },
  overlay: false,
  seriesFN: (stx: any, sd: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, sd, quotes);
  },
};

const vixStudy = {
  inputs: {},
  name: '_VIX',
  outputs: {
    Vix: bad,
  },
  overlay: false,
  seriesFN: (stx: any, sd: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, sd, quotes);
  },
};

const rVolStudy = {
  inputs: {},
  name: '_RVOL',
  outputs: {
    Rvol: good,
  },
  overlay: false,
  seriesFN: (stx: any, sd: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, sd, quotes);
  },
};

const rVolminStudy = {
  inputs: {},
  name: '_RVOLMIN',
  outputs: {
    Rvolmin: good,
  },
  overlay: false,
  seriesFN: (stx: any, sd: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, sd, quotes);
  },
};


function addStudy(name: string, outputs: any, enableMidline=true) {
  var margin = 0.04;
  CIQ.Studies.studyLibrary[name] = {
    name: name,
    inputs: {},
    outputs: outputs,
    yAxis: { min:0-margin, max:1+margin, idealTickSizePixels: 15, pretty: true},
    overlay: false,
    seriesFN: (stx: any, study: any, quotes: any) => {
      CIQ.Studies.displaySeriesAsLine(stx, study, quotes);
      if(enableMidline) {
        drawMidline(stx, study, quotes);
      }
    },
  };
};

var studies = [
  { name: "_R3M2R25", outputs: {
    TJ1: { color: '#00CD82', width: 2, pattern: 'solid' },
    R3M2H:  { color: 'rgba(254, 251, 72, 0.5)', width: 1, pattern: 'solid' }, // yellow
    R3M2N: { color: 'rgba(63, 99, 191, 0.7)', width: 1, pattern: 'solid' } // blue
  }, },
  { name: "_TJ1", outputs: { TJ1: '#00CD82' }, },
  { name: "_R1M2R25", outputs: { R1M2: '#00CD82' }, },
  { name: "_B2X5", outputs: { B2X5: '#00CD82' }, },
  { name: "EXP01", outputs: { Experiment1: '#00CD82' }, },
  { name: "EXP02", outputs: { Experiment2: '#FF3542' }, },
  { name: "EXP03", outputs: { Experiment3: '#00CD82' }, },
  { name: "EXP04", outputs: { Experiment4: '#FF3542' }, },
  { name: "EXP05", outputs: { Experiment5: '#00CD82' }, },
  { name: "EXP06", outputs: { Experiment6: '#FF3542' }, },
  { name: "EXP07", outputs: { Experiment7: '#00CD82' }, },
  { name: "EXP08", outputs: { Experiment8: '#FF3542' }, },
  { name: "EXP09", outputs: { Experiment9: '#00CD82' }, },
  { name: "EXP10", outputs: { Experiment10: '#FF3542' }, },
];
studies.forEach(study => addStudy(study.name, study.outputs));

CIQ.Studies.studyLibrary["MODEL_VEL"] = {
  name: `MODEL_VEL`,
  inputs: {},
  outputs: { Velocity: 'rgba(63, 99, 191, 0.7)' },
  overlay: false,
  seriesFN: (stx: any, study: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, study, quotes);
  },
};

const eventsStudy = {
  name: 'Events',
  inputs: {},
  outputs: {},
  yAxis: { position: 'none' },
};

const calculateRotation = (quotes: any[], study: any, rotation: number) => {
  let realtimeQuote = quotes[quotes.length - 1];
  let referenceQuote = quotes[0];
  let lastHighestQuote: any;
  let lastLowestQuote: any;

  quotes.forEach((quote: any, index: number) => {
    // Let's check if the prev quote have realtime data
    const prevQuote = quotes[index - 1];

    if (!prevQuote) return;

    if (prevQuote.tenPointObj && prevQuote.tenPointObj.direction === 'realtime') {
      prevQuote[`Point ${study.name}`] = null;
      prevQuote.tenPointObj = null;
    }

    // REVIEW ME: if the high are equals, what happens?
    // If the current quote is grow up
    if (quote.High >= referenceQuote.High) {
      if (!lastLowestQuote) lastLowestQuote = referenceQuote

      const points = quote.High - lastLowestQuote.Low;

      if (points >= rotation) {
        // When a candle could be the highest but it has 10 points or more in low
        if (referenceQuote.tenPointObj && referenceQuote.tenPointObj.direction === 'up') {
          const fallPoint = referenceQuote.High - quote.Low;

          if (fallPoint >= rotation) {
            quote[`Point ${study.name}`] = quote.Low;
            quote.tenPointObj = {
              count: fallPoint,
              direction: 'down',
              price: quote.Low
            };

            lastLowestQuote = quote;
            referenceQuote = lastLowestQuote;

            return;
          } else {
            if (quote.High >= lastHighestQuote.High) {
              referenceQuote[`Point ${study.name}`] = null;
              referenceQuote.tenPointObj = null;
            } else {
              quote[`Point ${study.name}`] = quote.Low;
              quote.tenPointObj = {
                count: -fallPoint,
                direction: 'realtime',
                price: quote.Low
              };
              return;
            }
          }
        }

        quote[`Point ${study.name}`] = quote.High;
        quote.tenPointObj = {
          count: points,
          direction: 'up',
          price: quote.High
        };

        lastHighestQuote = quote;
        referenceQuote = lastHighestQuote;

        return;
      } else {
        if (referenceQuote.tenPointObj) {
          quote[`Point ${study.name}`] = quote.High;
          quote.tenPointObj = {
            count: points,
            direction: 'realtime',
            price: quote.High
          };
        }
      }
    // If the current quote is grow down
    } else if (quote.High < referenceQuote.High) {
      if (!lastHighestQuote) lastHighestQuote = referenceQuote

      const points = lastHighestQuote.High - quote.Low;

      if (points >= rotation) {
        // When a candle could be the lowest but it has 10 points or more in high
        if (referenceQuote.tenPointObj && referenceQuote.tenPointObj.direction === 'down') {
          const raisePoint = quote.High - referenceQuote.Low;

          if (raisePoint >= rotation) {
            quote[`Point ${study.name}`] = quote.High;
            quote.tenPointObj = {
              count: raisePoint,
              direction: 'up',
              price: quote.High
            };

            lastHighestQuote = quote;
            referenceQuote = lastHighestQuote;
            return;
          } else {
            if (quote.Low <= lastLowestQuote.Low) {
              referenceQuote[`Point ${study.name}`] = null;
              referenceQuote.tenPointObj = null;
            } else {
              quote[`Point ${study.name}`] = quote.High;
              quote.tenPointObj = {
                count: raisePoint,
                direction: 'realtime',
                price: quote.High
              };
              return;
            }
          }
        }

        quote[`Point ${study.name}`] = quote.Low;
        quote.tenPointObj = {
          count: points,
          direction: 'down',
          price: quote.Low
        };

        lastLowestQuote = quote;
        referenceQuote = lastLowestQuote;

        return;
      } else {
        if (referenceQuote.tenPointObj) {
          quote[`Point ${study.name}`] = quote.Low;
          quote.tenPointObj = {
            count: points,
            direction: 'realtime',
            price: quote.Low
          };
        }
      }
    }

    // Let's review if the current quote is highest than the last highest quote
    if (lastHighestQuote && (quote.High >= lastHighestQuote.High)) {
      if (!lastLowestQuote) lastLowestQuote = referenceQuote

      const points = quote.High - lastLowestQuote.Low;

      lastHighestQuote[`Point ${study.name}`] = null;
      lastHighestQuote.tenPointObj = null;

      quote[`Point ${study.name}`] = quote.High;
      quote.tenPointObj = {
        count: points,
        direction: 'up',
        price: quote.High
      };

      lastHighestQuote = quote;
      referenceQuote = quote;
    }
    // Let's review if the current quote is lowest than the last lowest quote
    if (lastLowestQuote && (quote.Low <= lastLowestQuote.Low)) {
      if (!lastHighestQuote) lastHighestQuote = referenceQuote

      const points = lastHighestQuote.High - quote.Low;

      lastLowestQuote[`Point ${study.name}`] = null;
      lastLowestQuote.tenPointObj = null;

      quote[`Point ${study.name}`] = quote.Low;
      quote.tenPointObj = {
        count: points,
        direction: 'down',
        price: quote.Low
      };

      lastLowestQuote = quote;
      referenceQuote = quote;
    }
  });

  if (referenceQuote.tenPointObj) {
    let currentMarket;

    // If the reference is the highest point
    if (referenceQuote.tenPointObj.direction === 'up') {
      if (realtimeQuote.tenPointObj && realtimeQuote.tenPointObj.direction === 'up') {
        currentMarket = realtimeQuote.High - lastLowestQuote.Low;
      } else {
        currentMarket = realtimeQuote.Low - lastHighestQuote.High;
      }
    }

    // If the reference is the lowest point
    if (referenceQuote.tenPointObj.direction === 'down') {
      if (realtimeQuote.tenPointObj && realtimeQuote.tenPointObj.direction === 'down') {
        currentMarket = realtimeQuote.Low - lastHighestQuote.High;
      } else {
        currentMarket = realtimeQuote.High - lastLowestQuote.Low;
      }
    }

    realtimeQuote.box = currentMarket;
  }
};

const drawMidline = (stx: any, study: any, quotes: any) => {
  const ctx = stx.chart.context;
  const panel = stx.panels[study.panel];
  stx.startClip(panel.name);

  const yAxis = stx.getYAxisByName(panel, study.name);
  try {
    if(stx.chart.dataSet.length > 0) {
      var x = stx.pixelFromDate(stx.chart.dataSet[stx.chart.dataSet.length-1].Date, stx.chart);
      const y = stx.pixelFromPrice(0.5, panel, yAxis);
  
      var color = 'rgba(110, 160, 255, .5)';
      ctx.fillStyle = color;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.strokeRect(0, y, x, 1);  
    }
  }
  catch(e) {
    console.error(e);
  }
  stx.endClip();
}

const drawLineAndPointsRotation = (stx: any, study: any, quotes: any) => {
  const ctx = stx.chart.context;
  const panel = stx.panels[study.panel];
  const yAxis = stx.getYAxisByName(panel, study.name);
  stx.startClip(panel.name);
  let color = "#FFFFFF";
  const pointWidth = Math.min(
    Math.max(8, Math.floor(stx.chart.tmpWidth / 2)),
    12
  );
  quotes.forEach((quote: any, index: number) => {
    if (!quote || !quote.tenPointObj) return;

    let x0 = stx.pixelFromBar(index, panel.chart);
    const y0 = stx.pixelFromPrice(quote.tenPointObj.price, panel, yAxis);
    const countText = quote.tenPointObj.count
    const textCountWidth = ctx.measureText(countText).width;
    let translation = 0
    let translationSign = 1

    switch (quote.tenPointObj.direction) {
      case 'up':
        translation = -2
        translationSign = -1
        break;
      case 'down':
        translation = 2
        translationSign = 1
        break;
    }

    // Point (circle) and count text
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = pointWidth * 0.1;
    ctx.stxCircle(x0, y0, pointWidth * 0.7, true);
    ctx.font = "bold 16px Lato, sans-serif";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(countText, x0 - (textCountWidth / 2), y0 + translation + ((pointWidth + 8) * translationSign));

    // Rectangle and text for realtime box counter

    if (typeof quote.box === 'number') {
      const boxText = quote.box;
      const textBoxWith = ctx.measureText(boxText).width;
      const isPositiveOrNegative = quote.box >= 0 ? '+' : '';

      ctx.lineWidth = 2;
      ctx.strokeRect(x0 + 20, y0 - 14, textBoxWith + 33, 30);
      ctx.font = "bold 20px Lato, sans-serif";
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText(`${isPositiveOrNegative} ${boxText}`, x0 + 26, y0);
    }
  });
  stx.endClip();
}

const tenPointRotationStudy = {
  calculateFN: (stx: any, study: any) => {
    const model = stx.panels.chart.display
    const quotes = study.chart.scrubbed;

    if (!quotes) return;

    if (model.includes('ES')) calculateRotation(quotes, study, 10);
  },
  inputs: {},
  name: "_ES 10pt Rotation",
  outputs: {
    "Point": "#FFFFFF"
  },
  overlay: true,
  seriesFN: (stx: any, study: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, study, quotes);

    drawLineAndPointsRotation(stx, study, quotes)
  }
};
const twentysixPointRotationStudy = {
  calculateFN: (stx: any, study: any) => {
    const model = stx.panels.chart.display
    const quotes = study.chart.scrubbed;

    if (!quotes) return;

    if (model.includes('NQ')) calculateRotation(quotes, study, 26);
  },
  inputs: {},
  name: "_NQ 26pt Rotation",
  outputs: {
    "Point": "#FFFFFF"
  },
  overlay: true,
  seriesFN: (stx: any, study: any, quotes: any) => {
    CIQ.Studies.displaySeriesAsLine(stx, study, quotes);

    drawLineAndPointsRotation(stx, study, quotes)
  }
};
const multistepStudy = {
  name: '_Multistep',
  inputs: { "Channel Fill": true },
  outputs: {
    "MS Channel": '#FFFFFF',
    "MS Bottom": '#FFFFFF',
    "MS Top": '#FFFFFF'
  },
  overlay: true,
  calculateFN: (stx: any, study: any) => {
    const quotes = study.chart.scrubbed;
    const lastQuote = quotes[quotes.length - 1]
    const multistep = lastQuote.multistep;

    if (!multistep) return;

    multistep.p_future.forEach((future: number, index: number) => {
      const time = moment(multistep.t).add(index, 'minutes');

      if (index === 0) {
        lastQuote[`MS Bottom ${study.name}`] = multistep.err_lower[index];
        lastQuote[`MS Top ${study.name}`] = multistep.err_upper[index];
        lastQuote[`MS Channel ${study.name}`] = multistep.p_future[index];
      } else {
        quotes.push({
          DT: time.toDate(),
          Date: time.format("YYYYMMDDhhmmssSSS"),
          [`MS Bottom ${study.name}`]: multistep.err_lower[index],
          [`MS Top ${study.name}`]: multistep.err_upper[index],
          [`MS Channel ${study.name}`]: multistep.p_future[index],
        })
      }
    });
  },
  seriesFN: CIQ.Studies.displayChannel,
};

CIQ.Studies.studyLibrary.obosInline = obosInlineStudy;
CIQ.Studies.studyLibrary.LAWR = LAWRStudy;
CIQ.Studies.studyLibrary.obosIndividual = obosIndividualStudy;
CIQ.Studies.studyLibrary.vix = vixStudy;
CIQ.Studies.studyLibrary.rVol = rVolStudy;
CIQ.Studies.studyLibrary.rVolmin = rVolminStudy;
CIQ.Studies.studyLibrary.events = eventsStudy;
CIQ.Studies.studyLibrary.tenPointRotation = tenPointRotationStudy;
CIQ.Studies.studyLibrary.twentysixPointRotation = twentysixPointRotationStudy;
CIQ.Studies.studyLibrary.multistep = multistepStudy;

const initialState = fromJS({
  showStudyModal: false,
  studyHelper: null,
  studyList: CIQ.Studies.studyLibrary,
  studyOverlay: {
    left: 0,
    show: false,
    top: 0,
  },
});

export default handleActions({
  [CHARTIQ_STUDY_TOGGLE_STUDY_OVERLAY]: (state: any, { payload }: any) => {
    const flipOverlay = !state.get('studyOverlay').toJS().show;
    return state
      .set('studyHelper', flipOverlay ? new CIQ.Studies.DialogHelper(payload) : null)
      .set('studyOverlay', fromJS({ show: flipOverlay, top: payload.stx.cy, left: payload.stx.cx }));
  },
  [CHARTIQ_STUDY_ADD_STUDY]: state => state,
  [CHARTIQ_STUDY_OPEN_STUDY_MODAL]: (state: any, { payload }: any) => state.set('studyOverlay', fromJS({ show: false, top: 0, left: 0 }))
    .set('showStudyModal', true)
    // TODO: right now we are storing DialogHelper as a mutable in the immutable structure. This should
    // be passed in as a param and not stored in the store
    .set('studyHelper', payload.stx ? new CIQ.Studies.DialogHelper(payload) : state.get('studyHelper')),
  [CHARTIQ_STUDY_CLOSE_STUDY_MODAL]: (state: any) => state
    .set('studyOverlay', fromJS({ show: false, top: 0, left: 0 }))
    .set('showStudyModal', false)
    .set('studyHelper', null),
  [CHARTIQ_STUDY_UPDATE_STUDY]: (state: any) => state
    .set('studyOverlay', fromJS({ show: false, top: 0, left: 0 }))
    .set('showStudyModal', false)
    .set('studyHelper', null),
  [CHARTIQ_STUDY_REMOVE_STUDY]: (state: any) => state.set('studyOverlay', fromJS({ show: false, top: 0, left: 0 })),
}, initialState);
